// screens/create-product-mgt/ProductCreation.jsx
import React, { useState } from 'react';
import { Card, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import Header from '../../layouts/Header';
import AddProduct from './AddProduct';
import AllocateMenuPrice from './AllocateMenuPrice';
import AllocateAddon from './AllocateAddon';
import AllocateIngredients from './AllocateIngredients';
import { toast } from 'react-toastify';

const ProductCreation = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [productCreated, setProductCreated] = useState(false);

  // Store all product data
  const [productData, setProductData] = useState({
    // Step 1: Product Details
    product: {
      sequenceNumber: "",
      category: "",
      name: "",
      description: "",
      hsn: "",
      taxPercent: "",
      makingTime: "",
      mrp: "",
      offerPrice: "",
      productCost: 0,
      productTagId: "",
      productTagName: "",
      keywords: [],
      foodType: "",
      classType: "",
      calories: "",
      weight: "",
      weightUnit: "",
      isEventProduct: "",
      isNewProduct: "",
      isFreebieProduct: "", // Added new field
      orderType: "ALL",
      excludedOutlets: [],
      productsNature: [],
      displayImage: null,
      coverPicture: null
    },

    // Step 2: Menu Group Pricing
    pricing: {
      menuGroups: [],
      pricingStrategy: "uniform",
      uniformPrice: 0,
      isAllocated: false
    },

    // Step 3: Add-ons
    addons: {
      selectedAddons: [],
      maxSelections: 0,
      isRequired: false
    },

    // Step 4: Ingredients
    ingredients: {
      items: [],
      totalCost: 0,
      wastagePercentage: 5,
      recipeInstructions: ""
    }
  });

  const steps = [
    { id: 1, title: "Product Details", component: AddProduct },
    { id: 2, title: "Menu Group Pricing", component: AllocateMenuPrice },
    { id: 3, title: "Add-ons Allocation", component: AllocateAddon },
    { id: 4, title: "Ingredients Setup", component: AllocateIngredients }
  ];

  // Update data for specific step
  const updateStepData = (step, data) => {
    setProductData(prev => ({
      ...prev,
      [step]: { ...prev[step], ...data }
    }));
  };

  const handleNext = () => {
    setErrors([]);
    setProductCreated(true);
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setErrors([]);

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Add this function - Reset the wizard 
  const handleComplete = () => {
    // Reset all form data
    setProductData({
      product: {
        sequenceNumber: "",
        category: "",
        name: "",
        description: "",
        hsn: "",
        taxPercent: "",
        makingTime: "",
        mrp: "",
        offerPrice: "",
        productCost: 0,
        productTagId: "",
        productTagName: "",
        keywords: [],
        foodType: "",
        classType: "",
        calories: "",
        weight: "",
        weightUnit: "",
        isEventProduct: "",
        isNewProduct: "",
        isFreebieProduct: "", // Added new field
        orderType: "ALL",
        excludedOutlets: [],
        productsNature: [],
        displayImage: null,
        coverPicture: null
      },
      pricing: {
        menuGroups: [],
        pricingStrategy: "uniform",
        uniformPrice: 0,
        isAllocated: false
      },
      addons: {
        selectedAddons: [],
        maxSelections: 0,
        isRequired: false
      },
      ingredients: {
        items: [],
        totalCost: 0,
        wastagePercentage: 5,
        recipeInstructions: ""
      }
    });
    localStorage.clear("productId");
    setProductCreated(false);
    // Go back to Step 1
    setCurrentStep(1);
    setErrors([]);
    setIsSubmitting(false);

    // Show success message
    toast.success("Product created successfully! You can now create another product.", {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  };

  console.log("Product Created In parent :", productCreated);
  

  const renderStep = () => {
    const StepComponent = steps?.find(step => step?.id === currentStep)?.component;

    if (!StepComponent) return null;

    // Different props for AllocateIngredients (Step 4)
    if (currentStep === 4) {
      return (
        <AllocateIngredients
          data={productData}
          onNext={handleNext}
          onBack={handleBack}
          updateData={updateStepData}
          currentStep={currentStep}
          totalSteps={steps?.length}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          setErrors={setErrors}
          onComplete={handleComplete} // Pass the complete function
        />
      );
    }

    // For other steps
    return (
      <StepComponent
        data={productData}
        onNext={handleNext}
        onBack={handleBack}
        updateData={updateStepData}
        currentStep={currentStep}
        totalSteps={steps?.length}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        setErrors={setErrors}
        isProductCreated={productCreated}
      />
    );
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="container" style={{ backgroundColor: "white" }}>
          <Row className="pt-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="fw-semibold mb-1">CREATE PRODUCT</h5>
                  <p className="text-muted mb-0">
                    Step {currentStep} of {steps.length}: {steps.find(s => s.id === currentStep)?.title}
                  </p>
                </div>
                <div className="badge bg-primary">
                  Step {currentStep}
                </div>
              </div>
            </Col>
            <hr />
          </Row>

          {/* Progress Indicator */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between position-relative">
                <div className="progress-line" style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`
                }}></div>

                {steps?.map((step) => {
                  const isActive = step.id === currentStep;
                  const isCompleted = step.id < currentStep;

                  return (
                    <div key={step.id} className="text-center" style={{ flex: 1 }}>
                      <div className={`step-circle ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                        {isCompleted ? '✓' : step.id}
                      </div>
                      <div className="step-title mt-2">
                        {step.title}
                      </div>
                    </div>
                  );
                })}
              </div>

              <style>{`
                .progress-line {
                  position: absolute;
                  top: 20px;
                  left: 0;
                  height: 3px;
                  background: #007bff;
                  transition: width 0.3s ease;
                  z-index: 1;
                }
                .step-circle {
                  width: 40px;
                  height: 40px;
                  line-height: 40px;
                  border-radius: 50%;
                  background: #e9ecef;
                  color: #6c757d;
                  font-weight: 600;
                  margin: 0 auto;
                  position: relative;
                  z-index: 2;
                  transition: all 0.3s ease;
                }
                .step-circle.active {
                  background: #007bff;
                  color: white;
                  transform: scale(1.1);
                }
                .step-circle.completed {
                  background: #28a745;
                  color: white;
                }
                .step-title {
                  font-size: 12px;
                  font-weight: 500;
                }
              `}</style>
            </Col>
          </Row>

          {/* Error Display */}
          {errors.length > 0 && (
            <Row className="mb-3">
              <Col>
                <Alert variant="danger" onClose={() => setErrors([])} dismissible>
                  <Alert.Heading>Please fix the following:</Alert.Heading>
                  <ul className="mb-0">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Step Content */}
          <Row>
            <Col>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                  {renderStep()}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductCreation;